import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LIBRARY_PUBLISH_TEXT,
  type LibraryFolder,
  type LibraryPublishMode,
  type LibraryTeamBoard,
} from "@kaba/shared";
import { api } from "../../lib/api";
import { toast } from "../../lib/toast";
import { Button, Sheet } from "../ui/ui";

const HINT: Record<LibraryPublishMode, string> = {
  none: "Nur Mitglieder des Teams sehen den Ordner (in den Teameinstellungen).",
  all: "Erscheint in jedem Board des Teams unter „Dateien → Team“ – auch in Boards, die später dazukommen.",
  boards: "Erscheint nur in den gewählten Boards.",
};

/**
 * Team-Ordner für Boards freigeben (0.57): Nur im Team, alle Boards des Teams oder ausgewählte.
 * Wer ein Board sehen darf, sieht dort die freigegebenen Ordner samt Unterordnern und kann ihre Dateien
 * an Karten hängen. Zur Auswahl stehen nur Boards, die die freigebende Person selbst sieht.
 */
export function PublishDialog({
  folder,
  teamId,
  onClose,
  onSaved,
}: {
  folder: LibraryFolder | null;
  teamId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const open = !!folder && !!teamId;
  const boards = useQuery({
    queryKey: ["library-team-boards", teamId],
    queryFn: () =>
      api.get<LibraryTeamBoard[]>(`/api/teams/${teamId}/library/boards`),
    enabled: open,
  });
  const [mode, setMode] = useState<LibraryPublishMode>("none");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!folder) return;
    setMode(folder.publish?.mode ?? "none");
    setPicked(new Set(folder.publish?.boardIds ?? []));
  }, [folder]);
  const list = boards.data ?? [];
  // Freigaben an Boards, die die Person nicht sieht, bleiben erhalten
  const hidden = [...picked].filter((id) => !list.some((b) => b.id === id));

  const save = async () => {
    if (!folder) return;
    if (mode === "boards" && !picked.size)
      return toast.error("Wähle mindestens ein Board.");
    setBusy(true);
    try {
      await api.put(`/api/library/folders/${folder.id}/publish`, {
        mode,
        boardIds: mode === "boards" ? [...picked] : [],
      });
      toast.info(
        mode === "none"
          ? `„${folder.name}“ ist nur im Team sichtbar.`
          : `„${folder.name}“ ist freigegeben.`,
      );
      onSaved();
      onClose();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={folder ? `„${folder.name}“ für Boards freigeben` : "Freigeben"}
      side="center"
    >
      <div className="flex flex-col gap-3 p-4">
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-sm font-semibold">Sichtbar in</legend>
          {(Object.keys(LIBRARY_PUBLISH_TEXT) as LibraryPublishMode[]).map(
            (m) => (
              <label
                key={m}
                className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-black/[0.03] dark:hover:bg-white/5"
              >
                <input
                  type="radio"
                  name="publish-mode"
                  className="mt-0.5 size-5 shrink-0 accent-[var(--color-accent)]"
                  checked={mode === m}
                  onChange={() => setMode(m)}
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium">
                    {LIBRARY_PUBLISH_TEXT[m]}
                  </span>
                  <span className="block text-xs text-[var(--ink-soft)]">
                    {HINT[m]}
                  </span>
                </span>
              </label>
            ),
          )}
        </fieldset>
        {mode === "boards" && (
          <fieldset className="flex flex-col">
            <legend className="mb-1 text-sm font-semibold">Boards</legend>
            {boards.isLoading ? (
              <p className="text-sm text-[var(--ink-soft)]">Lädt …</p>
            ) : list.length ? (
              <ul className="flex max-h-64 flex-col overflow-y-auto rounded-xl border border-[var(--line)]">
                {list.map((b) => (
                  <li
                    key={b.id}
                    className="border-b border-[var(--line)] last:border-b-0"
                  >
                    <label className="flex min-h-11 cursor-pointer items-center gap-3 px-3">
                      <input
                        type="checkbox"
                        className="size-5 shrink-0 accent-[var(--color-accent)]"
                        checked={picked.has(b.id)}
                        onChange={(e) =>
                          setPicked((p) => {
                            const n = new Set(p);
                            if (e.target.checked) n.add(b.id);
                            else n.delete(b.id);
                            return n;
                          })
                        }
                      />
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {b.title}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm">Dieses Team hat noch keine Boards.</p>
            )}
            {hidden.length > 0 && (
              <p className="mt-1 text-xs text-[var(--ink-soft)]">
                Dazu {hidden.length === 1 ? "1 Board" : `${hidden.length} Boards`},
                die du nicht siehst – die Freigabe dort bleibt bestehen.
              </p>
            )}
          </fieldset>
        )}
        <p className="text-xs text-[var(--ink-soft)]">
          Die Freigabe gilt für den Ordner samt Unterordnern. Wer das Board
          sehen darf, sieht die Dateien und kann sie (mit Bearbeitungsrecht) an
          Karten hängen. Bereits angehängte Dateien bleiben in den Karten, auch
          wenn du die Freigabe zurücknimmst.
        </p>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void save()}>
            Speichern
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
